import React, { createContext, useContext, useEffect, useState } from 'react';
import { GoogleTokenResponse, refreshGoogleToken } from '../services/googleAppsScriptApi';
import { jwtDecode } from 'jwt-decode';
import { googleLogout } from '@react-oauth/google';

interface AuthContextType {
  accessToken: string | null;
  idToken: string | null;
  refreshToken: string | null;
  roles: [string] | null;
  user: AuthUser | null;
  expiresAt: number | null;
  login: (token: GoogleTokenResponse) => void;
  logout: () => void;
  refresh: (minTimeToExpireMs?: number) => void; 
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthUser {
  given_name: string
  family_name: string
  email: string
  picture: string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [roles, setRoles] = useState<[string] | null>(null);
  const [expiresAt, setExpiresAtToken] = useState<number | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load tokens from localStorage on app start
  useEffect(() => {
    const storedToken = localStorage.getItem('accessToken');
    const storedIdToken = localStorage.getItem('idToken');
    const storedRefreshToken = localStorage.getItem('refreshToken');
    const roles = localStorage.getItem('roles') ?? "[]"; // TODO bsesso after some while remove empty array 2025.04.08
    const storedExpiresAt = localStorage.getItem('expiresAt');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedIdToken && storedRefreshToken && storedExpiresAt && storedUser && roles) {
      setAccessToken(storedToken);
      setIdToken(storedIdToken);
      setRefreshToken(storedRefreshToken);
      setRoles(JSON.parse(roles));
      setExpiresAtToken(Number(storedExpiresAt));
      setUser(JSON.parse(storedUser));
    }
    setIsLoading(false);
  }, []);

  // Save tokens when user logs in
  const login = (tokenResponse: GoogleTokenResponse) => {
    const token = tokenResponse.google_token;
    setAccessToken(token.access_token);
    setIdToken(token.id_token);
    setRefreshToken(token.refresh_token);
    setRoles(tokenResponse.roles);

    // console.log(token.expires_in);
    const expAt = Math.floor(Date.now() / 1000) + token.expires_in;
    // console.log(expAt);
    // console.log(expiresAt);
    setExpiresAtToken(expAt);
    // console.log(expiresAt);


    const decodedToken: AuthUser = jwtDecode(token.id_token);
    const userInfo: AuthUser = {
      given_name: decodedToken.given_name,
      family_name: decodedToken.family_name,
      email: decodedToken.email,
      picture: decodedToken.picture
    }
    setUser(userInfo);

    localStorage.setItem('accessToken', token.access_token);
    localStorage.setItem('idToken', token.id_token);
    localStorage.setItem('refreshToken', token.refresh_token);
    localStorage.setItem('roles', JSON.stringify(tokenResponse.roles));
    localStorage.setItem('expiresAt', String(expAt));
    localStorage.setItem('user', JSON.stringify(userInfo));
  };

  // Clear tokens on logout
  const logout = () => {
    setAccessToken(null);
    setIdToken(null);
    setRefreshToken(null);
    setRoles(null);
    setExpiresAtToken(null);
    setUser(null);

    // Remove tokens and user data from localStorage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('roles');
    localStorage.removeItem('expiresAt');
    localStorage.removeItem('user');
  };

  const refresh = (minTimeToExpireMs: number = 1e10) => {
    try {
      if (accessToken && refreshToken) {
        const nowMs = Date.now();
        const expireDateMs = expiresAt! * 1000;
        // console.log("to expire: ", (expireDateMs - nowMs) / 1000 / 60)

        if (nowMs + minTimeToExpireMs >= expireDateMs) {
          // console.log("refreshing");
          refreshGoogleToken(refreshToken).then(res => {
            // console.log("new token: ", res!.access_token);
            if (res) {
              const newExp = Math.floor(Date.now() / 1000) + res!.expires_in;

              setExpiresAtToken(newExp);
              localStorage.setItem('expiresAt', String(newExp));

              setAccessToken(res!.access_token);
              localStorage.setItem('accessToken', res!.access_token);
            }
          })
        }
      }
    } catch (e) {
      console.error('Error refreshing token. Logging out...', e);
      googleLogout()
      logout()
    }
  }

  useEffect(() => {
    const interval = setInterval(refresh, 1000 * 60 * 20); // Every 20 minutes
    return () => clearInterval(interval);
  }, [refreshToken]);

  return (
    <AuthContext.Provider value={{ accessToken, idToken, refreshToken, roles, expiresAt, user, login, logout, refresh, isAuthenticated: !!accessToken, isLoading: isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
